import Big from 'big.js';

import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {AbstractControl, FormArray, FormBuilder, FormGroup, ValidationErrors, Validators} from '@angular/forms';
import {NgbActiveModal, NgbDate, NgbDateParserFormatter, NgbModal} from '@ng-bootstrap/ng-bootstrap';
import * as moment from 'moment';
import {combineLatest, of, ReplaySubject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';

import {AuthenticationService} from '@app/shared/services/authentication.service';
import {DealerProfile} from '@app/domain/model/dealer-profile';
import {DuplicatesCheckParams} from '@app/domain/model/duplicates-check-params';
import {ReportType} from '@app/domain/model/report-type';
import {Condition, ConditionService} from '@app/feature/condition';
import {Species, SpeciesService} from '@app/feature/species';
import {AqSpecies} from '@app/feature/species/aq-species.model';
import {FoodSpecies} from '@app/feature/species/food-species.model';
import {Vessel, VesselService} from '@app/feature/vessel';
import {DuplicatesCheckService} from '@app/shared/services/duplicates-check.service';
import {validateReportType} from '@app/shared/validators/report-type.validator';
import {validateVesselId, validVesselInfo} from '@app/shared/validators/vessel.validator';

import {DatePickerFormatter} from '@app/util/date-picker-formatter';
import {LicenseStatus} from '@app/feature/seller/license-status';
import {QuerySellersService} from '@app/feature/seller/query-sellers.service';
import {Seller} from '@app/feature/seller/seller';
import {PurchaseReceiptReport} from '../purchase-receipt-report/purchase-receipt-report.model';
import {ReportingPeriod} from '../reporting-period';
import {PurchaseReportCreationEditingService} from './purchase-report-creation-editing.service';
import {MarketType} from '@app/domain/model/market-type';
import {AppError, AppErrorDto} from '@app/domain/model/error';
import {inRange, positiveNumber} from '@app/shared/validators/purchase.validator';
import {UserAuthority} from '@app/domain/model/user-authority';
import {DateFormatter} from '@app/util/date-formatter';
import {CURRENCY_ROUNDING_MODE, divideBig, multiplyBig} from '@app/util/currency';
import {FormArrayUtils} from '@app/util/form-array-utils';
import {HttpErrorResponse} from '@angular/common/http';
import {Purchase} from '@app/domain/model/purchase';
import {FormControlUtils} from '@app/util/form-group-utils';
import {StatusCheckParams} from '@app/domain/model/status-check-params';
import {StatusCheckService} from '@app/shared/services/status-check.service';
import {ConfirmationComponent} from '@app/shared/confirmation/confirmation.component';


@Component({
  providers: [{provide: NgbDateParserFormatter, useClass: DatePickerFormatter}],
  selector: 'app-purchase-report-creation-editing',
  templateUrl: './purchase-report-creation-editing.component.html',
  styleUrls: ['./purchase-report-creation-editing.component.scss']
})
export class PurchaseReportCreationEditingComponent implements OnInit, OnDestroy {

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  // make the enums' values accessible from template
  LicenseStatus: typeof LicenseStatus = LicenseStatus;
  ReportType: typeof ReportType = ReportType;
  MarketType: typeof MarketType = MarketType;
  reportAdded = false;
  today: Date = new Date();
  days: Date[] = [];

  // if period is empty
  element: HTMLElement;

  @Input()
  profile: DealerProfile;

  // input parameter - reporting period for which we are adding purchase report
  @Input()
  period: ReportingPeriod;

  @Input()
  selectedYear: number;

  @Input()
  marketTypes: Set<MarketType>;

  @Input()
  purchaseReport: PurchaseReceiptReport;

  @Input()
  isCreationMode = true;
  vesselRequired = false;
  showBoatNotUsedCheckbox = false;
  // report form
  reportForm: FormGroup;

  reportTotal = Big(0);

  // selected seller
  seller: Seller;

  species: Species;
  loading = false;
  showCancelWarning: boolean;
  showRemovePurchaseWarning: number;
  showRemoveAmendmentWarning: number;
  vesselEnteredManually = false;

  lastDuplicatesCheckParams: DuplicatesCheckParams;
  initialDuplicatesCheckParams: DuplicatesCheckParams = null;
  showDuplicateWarning = false;
  duplicatesChecking = false;

  lastStatusCheckParams: StatusCheckParams;
  statusChecking = false;

  submitActive = false;

  purchaseWarnings: ValidationErrors = {};

  sellerStatus: LicenseStatus = null;
  minDate: NgbDate = this.firstPickableDay();
  maxDate: NgbDate = this.lastPickableDay();

  constructor(private sellersReader: QuerySellersService,
              private speciesService: SpeciesService,
              private vesselService: VesselService,
              private conditionService: ConditionService,
              private duplicatesCheckService: DuplicatesCheckService,
              private statusCheckService: StatusCheckService,
              private reportProcessor: PurchaseReportCreationEditingService,
              private authenticator: AuthenticationService,
              private formBuilder: FormBuilder,
              private activeModal: NgbActiveModal,
              private readonly modalService: NgbModal) {
  }

  private getDealerId() {
    const executor = this.authenticator.getCurrentUser();
    return executor.hasAuthority(UserAuthority.DEALER_INFO) ? this.profile.id : null;
  }

  licenseAutocompleteFormatter = (seller: Seller) => seller ? `${seller.licenseNumber} (${seller.name})` : null;

  speciesAutocompleteFormatter = (species: Species) => {
    if (!species) {
      return null;
    }
    const executor = this.authenticator.getCurrentUser();

    if (executor.hasAuthority(UserAuthority.DEALER_INFO)) {
      return species.localNames ? `${species.id} - ${species.name} (${species.localNames})` : `${species.id} - ${species.name}`;
    } else {
      return species.localNames ? `${species.name} (${species.localNames})` : species.name;
    }
  }

  vesselAutocompleteResultFormatter = (vessel: Vessel) => {
    if (!vessel) {
      return null;
    }

    if (typeof vessel === 'string') {
      return vessel;
    }

    return vessel.name ? `${vessel.registrationNumber} (${vessel.name})` : vessel.registrationNumber;
  }

  vesselAutocompleteInputFormatter = (vessel: Vessel) => vessel ? vessel.registrationNumber : null;

  conditionAutocompleteFormatter = (condition: Condition) => {
    if (!condition) {
      return null;
    }
    return `${condition.description} (${condition.code})`;
  }

  licenseAutocomplete = (term: string) => {
    if (term.length < 3) {
      return of([]);
    }

    return this.sellersReader.querySellers(term);
  }

  speciesAutocomplete = (term: string) => this.speciesService.getSpecies(term, this.reportForm.get('reportType').value);

  vesselAutocomplete = (term: string) => this.vesselService.getVessel(term, this.reportForm.get('seller').value.licenseNumber);

  conditionAutocomplete = (term: string) => this.conditionService.getCondition(term);

  ngOnInit() {
    this.initForm();
    this.setFormValue();
    if (this.period) {
      this.days = this.period.days();
    }
    if (this.isCreationMode && (this.marketTypes.size === 1)) {
      if (this.marketTypes.has(MarketType.NAQ)) {
        this.reportForm.get('reportType').setValue(ReportType.P);
      } else {
        this.reportForm.get('reportType').setValue(ReportType.PAQ);
      }
      this.reportForm.get('reportType').markAsTouched();
      this.reportForm.get('reportType').markAsDirty();
    }
    this.minDate = this.firstPickableDay();
    this.maxDate = this.lastPickableDay();
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  addAmendment() {
    const amendmentFormArray = this.reportForm.controls['amendments'] as FormArray;

    amendmentFormArray.push(this.formBuilder.group({
      name: ['', [Validators.required, Validators.maxLength(250)]],
      amount: ['', Validators.required]
    }));
  }

  addPurchase() {

    // Create new purchase, ...
    const purchase = this.formBuilder.group({
      species: [null, Validators.required],
      condition: [null],
      lbsBought: [null],
      piecesBought: [null],
      pricePerLb: [null],
      pricePerPc: [null],
      value: [null, Validators.required]
    });

    // ... append it to the purchases list, ...
    const purchases = this.reportForm.controls['purchases'] as FormArray;
    purchases.push(purchase);

    // ... initialize purchase validators, ...
    this.initPurchaseValidators(this.reportForm.get('reportType'));
  }

  private initVesselValidators() {
    const purchases = this.reportForm.controls['purchases'] as FormArray;
    this.vesselRequired = null != purchases.controls.find(purchase => {
      const species = (purchase as FormGroup).controls['species'].value;
      return species && species.vesselRequired;
    });
    if(this.vesselRequired){
      this.showBoatNotUsedCheckbox = true;
      this.vesselRequired = this.vesselRequired && !this.reportForm.get('boatNotUsed').value;
    } else {
      this.showBoatNotUsedCheckbox = false;
      this.reportForm.get('boatNotUsed').setValue(false);
    }

    const vessel = this.reportForm.controls['vessel'] as FormGroup;
    const id = vessel.controls['id'];
    const name = vessel.controls['name'];

    name.setValidators([Validators.maxLength(250)]);
    if (this.vesselRequired) {
      id.setValidators([Validators.required, validateVesselId]);
    } else {
      id.setValidators([validateVesselId]);
    }

    id.updateValueAndValidity();
    name.updateValueAndValidity();
    // id.markAsUntouched();
    // name.markAsUntouched();
  }

  private verifyPPU(purchaseIdx: number) {
    const purchases = this.reportForm.get('purchases') as FormArray;
    const purchase = purchases.at(purchaseIdx);

    if (purchase) {
      const idPrefix = 'purchase-' + purchaseIdx;
      this.purchaseWarnings[idPrefix + '-price-per-lb'] = null;
      this.purchaseWarnings[idPrefix + '-price-per-pc'] = null;
      const species = purchase.get('species').value as Species;
      if (species) {
        const price = species.lbsExempt ? purchase.get('pricePerPc').value : purchase.get('pricePerLb').value;
        if (price) {
          const id = species.lbsExempt ? idPrefix + '-price-per-pc' : idPrefix + '-price-per-lb';
          if ((species.minPrice && (price < species.minPrice)) || (species.maxPrice && (species.maxPrice < price))) {
            this.purchaseWarnings[id] = species;
          }
        }
      }
    }
  }

  private verifyWeight(purchaseIdx: number) {
    const purchases = this.reportForm.get('purchases') as FormArray;
    const purchase = purchases.at(purchaseIdx);

    if (purchase) {
      const id = 'purchase-' + purchaseIdx + '-lbs-bought';
      this.purchaseWarnings[id] = null;
      const lbsBought = purchase.get('lbsBought').value;
      const species = purchase.get('species').value as Species;
      if (lbsBought && species && !species.pieceExempt) {
        const piecesBought = purchase.get('piecesBought').value;
        if (piecesBought != 0) {
          const weightPerPiece = new Big(lbsBought).div(piecesBought);
          if ((species.minWeight && weightPerPiece.lt(species.minWeight)) || (species.maxWeight && !weightPerPiece.lte(species.maxWeight))) {
            this.purchaseWarnings[id] = species;
          }
        }
      }
    }
  }

  hasArrayError(parentName: string, index: number, controlName: string, errorCode?: string) {
    const parent = this.reportForm.get(parentName) as FormArray;
    const control = parent.at(index).get(controlName);
    const errors = control.errors;
    const error = errorCode && errors ? errors[errorCode] : null;
    return (errorCode ? error : !control.valid) && (control.touched);
  }

  hasError(controlName: string, errorCode?: string): boolean {
    const control = this.reportForm.get(controlName);
    const errors = control.errors;
    const error = errorCode && errors && errors[errorCode] ? errors[errorCode] : null;
    return (errorCode ? error : !control.valid) && (control.touched);
  }

  onPurchaseUnitsUpdate(index: number) {

    const purchases = this.reportForm.get('purchases') as FormArray;
    const purchase = purchases.at(index);
    this.updatePurchaseValue(purchase);
    this.verifyWeight(index);
    this.verifyPPU(index);
    this.updateTotal();
  }

  onPurchaseValueUpdate(index: number){
    const purchases = this.reportForm.get('purchases') as FormArray;
    const purchase = purchases.at(index);
    this.updatePurchasePPU(purchase);
    this.verifyPPU(index);
    this.updateTotal();

  }
  updatePurchaseValuesList(index?: number) {
    const purchases = this.reportForm.get('purchases') as FormArray;

    purchases.controls.forEach((control) => {
      this.updatePurchaseValue(control);
      this.updatePurchasePPU(control);
    });

  }

  onCancel() {
    if (this.showCancelWarning) {
      this.activeModal.close(this.reportAdded);
    }
    this.showCancelWarning = true;
  }

  onSubmit() {
    FormControlUtils.markAllTouched(this.reportForm);
    this.submitActive = false;
    if (this.reportForm.invalid) {
      return;
    }

    if (this.isCreationMode) {
      this.addPurchaseReport();
    } else {
      this.editPurchaseReport();
    }
  }

  submitAndContinue() {
    FormControlUtils.markAllTouched(this.reportForm);
    this.submitActive = false;
    if (this.reportForm.invalid) {
      return;
    }
    this.addPurchaseReport(true);
  }


  onSellerSelect(seller: Seller) {
    this.seller = seller;
    this.reportForm.patchValue({seller: seller});
  }

  onSellerEnter(seller: string) {
    if (seller === '') {
      this.reportForm.patchValue({seller: null});
    }
    this.reportForm.get('seller').markAsTouched();
  }

  onSpeciesSelect(species: Species, index: number) {
    const purchases = this.reportForm.get('purchases') as FormArray;
    const purchase =  purchases.at(index);
    const control = purchase.get('species');
    control.patchValue(species);

    this.validateReportType();
    this.initPurchaseValidators(this.reportForm.get('reportType'));
    this.clearAmounts(index);

    this.initVesselValidators();
    this.verifyPPU(index);
    this.verifyWeight(index);
    this.updatePurchaseValue(purchase);
    this.updateTotal();

  }

  onSpeciesFocusOut(species: string, index: number) {
    const purchases = this.reportForm.get('purchases') as FormArray;
    const control = purchases.at(index).get('species');
    if (species === '') {
      control.patchValue(null);
      control.markAsDirty();
    }
    control.markAsTouched();
  }

  onConditionSelect(condition: Condition, index: number) {
    const purchases = this.reportForm.get('purchases') as FormArray;
    const control = purchases.at(index).get('condition');
    control.patchValue(condition);
  }

  selectOnFocus($event) {
    const target = $event.target;
    if (target instanceof HTMLInputElement) {
      target.select();
    }
  }

  onConditionFocusOut(value: string, index: number) {
    const purchases = this.reportForm.get('purchases') as FormArray;
    const condition = purchases.at(index).get('condition');
    if (!condition) {
      condition.patchValue(null);
    }
    condition.markAsTouched();

  }

  onVesselSelect(vessel: Vessel) {
    this.reportForm.patchValue({
      vessel: {
        name: vessel.name ? vessel.name : '',
        id: vessel.registrationNumber ? vessel.registrationNumber : ''
      }
    });
    this.vesselEnteredManually = false;
  }

  onVesselEnter(evt: Event) {
    const vessel: string = (<HTMLInputElement>evt.target).value;
    if (vessel !== this.reportForm.get('vessel').get('id').value) {
      this.reportForm.patchValue({
        vessel: {
          id: vessel
        }
      });
      this.vesselEnteredManually = true;
    }
    this.reportForm.get('vessel').get('id').markAsTouched();
    this.reportForm.get('boatNotUsed').setValue(false);
    this.initVesselValidators();
  }
  onVesselNameEnter() {
    this.reportForm.get('vessel').get('name').markAsTouched();
    this.reportForm.get('boatNotUsed').setValue(false);
    this.initVesselValidators();
  }

  /**
   * @return datepicker friendly representation of first pickable date for the purchase report
   */
  firstPickableDay(): NgbDate {
    let d = this.reportDateMin();
    return new NgbDate(d.getFullYear(), 1 + d.getMonth(), d.getDate());
  }

  /**
   * @return datepicker friendly representation of last pickable date for the purchase report
   */
  lastPickableDay(): NgbDate {
    let d = this.reportDateMax();
    return new NgbDate(d.getFullYear(), 1 + d.getMonth(), d.getDate());
  }

  private getSunday(d: Date) {
    d = new Date(d);
    var day = d.getDay(),
      diff = d.getDate() - day; // adjust when day is sunday
    return new Date(d.setDate(diff));
  }

  private getSatarday(d: Date) {
    d = new Date(d);
    var day = d.getDay(),
      diff = d.getDate() + 6 - day; // adjust when day is sunday
    return new Date(d.setDate(diff));
  }

  isConditionNeeded(): boolean {
    const reportType = this.reportForm.get('reportType').value;
    return (ReportType.P === reportType);
  }

  isLbsBased(index: number): boolean {
    const reportType = this.reportForm.get('reportType').value;
    const purchases = this.reportForm.get('purchases') as FormArray;
    const species = purchases.at(index).get('species').value as Species;
    return (ReportType.P === reportType) && !(species && species.lbsExempt);
  }

  isPcsBased(index: number): boolean {
    const reportType = this.reportForm.get('reportType').value;
    const purchases = this.reportForm.get('purchases') as FormArray;
    const species = purchases.at(index).get('species').value as Species;
    return this.requiresPcs(reportType, species);
  }

  private requiresPcs(reportType: ReportType, species: Species) {
    return (ReportType.PAQ === reportType) || (species && species.lbsExempt);
  }

  removeAmendment(index: number) {
    if (this.showRemoveAmendmentWarning === index) {
      const amendments = this.reportForm.get('amendments') as FormArray;
      amendments.removeAt(index);
      this.reportForm.markAsDirty();
      this.updateTotal();
      this.showRemoveAmendmentWarning = null;
    } else {
      this.showRemoveAmendmentWarning = index;
    }
  }

  removePurchase(index: number) {
    if (this.showRemovePurchaseWarning === index) {
      const purchases = this.reportForm.get('purchases') as FormArray;
      purchases.removeAt(index);
      this.reportForm.markAsDirty();
      this.updateTotal();
      this.validateReportType();
      this.showRemovePurchaseWarning = null;
      this.purchaseWarnings = {};
      purchases.controls
        .forEach(
          (purchase, purchaseIdx) => {
            this.verifyWeight(purchaseIdx);
            this.verifyPPU(purchaseIdx);
          });
      this.initVesselValidators();
    } else {
      this.showRemovePurchaseWarning = index;
    }
  }

  updateTotal() {
    const purchases = this.reportForm.get('purchases') as FormArray;
    let totalAmount = purchases.controls
      .map(control => control.get('value').value || 0)
      .filter(maybeAmount => !isNaN(maybeAmount))
      .map(amount => Big(amount))
      .reduce((a: Big, b: Big) => a.add(b), Big(0));

    const amendments = this.reportForm.get('amendments') as FormArray;
    if (amendments.length > 0) {
      totalAmount = totalAmount.add(amendments.controls
        .map(
          control => control.get('amount').value || 0)
        .filter(maybeAmount => !isNaN(maybeAmount))
        .map(amount => Big(amount))
        .reduce((a: Big, b: Big) => a.add(b), Big(0))
      );
    }
    this.reportTotal = totalAmount
  }

  setInputCursorToEnd(event) {
    // We have to wait with the change of the cursor position, to do focus().
    setTimeout(() => {
      const inputValue = event.srcElement.value;
      event.srcElement.setSelectionRange(inputValue.length, inputValue.length);
    });
  }

  onReportTypeChange(reportType) {
    this.updatePurchaseValuesList();
  }

  private initForm() {
    this.reportForm = this.formBuilder.group({
      seller: [null, Validators.required],
      reportDate: ['', Validators.required],
      reportType: ['', [Validators.required, validateReportType()]],
      amendments: this.formBuilder.array([]),
      purchases: this.formBuilder.array([]),
      boatNotUsed: [false],
      vessel: this.formBuilder.group(
        {
          id: ['', [validateVesselId]],
          name: ['', Validators.maxLength(250)]
        }, {'validator': validVesselInfo}),
      remarks: ['', [Validators.maxLength(1000),Validators.pattern(/^[^"^\n]*$/)]],
      notifyFisher: ['']
    });

    this.subscribeDuplicatesChecking();
    this.subscribeStatusChecking();

    // this.reportForm.get('vessel.name').valueChanges
    //   .pipe(debounceTime(250), distinctUntilChanged())
    //   .pipe(takeUntil(this.destroyed$))
    //   .subscribe(
    //     $ => {
    //       this.reportForm.get('vessel.id').markAsTouched();
    //       this.reportForm.get('vessel.id').updateValueAndValidity();
    //     });

    this.reportForm.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        $ => {
          this.submitActive = this.sellerStatus !== LicenseStatus.INACTIVE && !FormControlUtils.anyErrorDisplayed(this.reportForm);
        });

    const reportType = this.reportForm.get('reportType');
    reportType.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        $ => this.initPurchaseValidators(reportType)
      );
  }

  private initPurchaseValidators(reportType: AbstractControl) {
    const purchases = this.reportForm.controls['purchases'] as FormArray;
    purchases.controls.forEach(control => {
      const lbsBought = control.get('lbsBought');
      const pricePerLb = control.get('pricePerLb');
      const piecesBought = control.get('piecesBought');
      const pricePerPc = control.get('pricePerPc');
      const value = control.get('value');
      const species = control.get('species').value;

      value.setValidators([positiveNumber(), inRange(0, 1000000000)]);
      if (this.requiresPcs(reportType.value, species)) {
        lbsBought.clearValidators();
        pricePerLb.clearValidators();
        piecesBought.setValidators([positiveNumber(), inRange(0, 1000000)]);
        pricePerPc.setValidators([positiveNumber(false), inRange(0, 1000000000, false)]);
      } else {
        lbsBought.setValidators([positiveNumber(), inRange(0, 1000000)]);
        pricePerLb.setValidators([positiveNumber(false), inRange(0, 1000000000, false)]);
        if (species && !species.pieceExempt) {
          piecesBought.setValidators([positiveNumber(), inRange(0, 1000000)]);
        } else {
          piecesBought.clearValidators();
        }
        pricePerPc.clearValidators();
      }

      lbsBought.updateValueAndValidity();
      pricePerLb.updateValueAndValidity();
      piecesBought.updateValueAndValidity();
      pricePerPc.updateValueAndValidity();
      if (control.get('species') && control.get('species').hasError('invalidSpecies')) {
        control.get('species').markAsTouched();
      }
    });
  }

  private clearAmounts(index: number) {

    const purchases = this.reportForm.get('purchases') as FormArray;
    const quickReport = purchases.at(index);

    quickReport.get('lbsBought').patchValue('');
    quickReport.get('pricePerLb').patchValue('');
    quickReport.get('piecesBought').patchValue('');
    quickReport.get('pricePerPc').patchValue('');
    quickReport.get('value').patchValue('');
    quickReport.markAsUntouched();

  }

  private resetForm() {
    this.reportForm.reset();

    const purchases = this.reportForm.get('purchases') as FormArray;
    FormArrayUtils.clearFormArray(purchases);
    const amendmentFormArray = this.reportForm.controls['amendments'] as FormArray;
    FormArrayUtils.clearFormArray(amendmentFormArray);
    this.addPurchase();
    this.showDuplicateWarning = false;
    this.duplicatesChecking = false;
  }

  private subscribeDuplicatesChecking() {
    combineLatest([
      this.reportForm.get('reportDate').valueChanges,
      this.reportForm.get('reportType').valueChanges,
      this.reportForm.get('seller').valueChanges
    ])
      .pipe(takeUntil(this.destroyed$))
      .subscribe((data) => {
        const dpc = DuplicatesCheckParams.fromFormObject(data);
        if (dpc.complete() && !dpc.equals(this.lastDuplicatesCheckParams) && !dpc.equals(this.initialDuplicatesCheckParams)) {

          this.lastDuplicatesCheckParams = dpc;
          this.duplicatesChecking = true;
          this.duplicatesCheckService.queryDuplicatesChecking(dpc, this.getDealerId())
            .subscribe(
              (reponse: boolean) => {
                this.showDuplicateWarning = reponse;
                this.duplicatesChecking = false;
              });
        }
      });
  }

  private subscribeStatusChecking() {
    combineLatest([
      this.reportForm.get('reportDate').valueChanges,
      this.reportForm.get('seller').valueChanges
    ])
      .pipe(takeUntil(this.destroyed$))
      .subscribe((data) => {
        const spc = StatusCheckParams.fromFormObject(data);
        if (spc.complete() && this.seller && !spc.equals(this.lastStatusCheckParams)) {

          this.lastStatusCheckParams = spc;
          this.statusChecking = true;
          this.statusCheckService.queryStatusChecking(spc)
            .subscribe(
              (response: string) => {
                if(this.sellerStatus === LicenseStatus.INACTIVE && LicenseStatus[response] === LicenseStatus.ACTIVE){
                  FormControlUtils.markAllTouched(this.reportForm, false);
                }
                this.sellerStatus = LicenseStatus[response];
                this.submitActive = this.sellerStatus !== LicenseStatus.INACTIVE;


                this.statusChecking = false;
              });
        }
      });
  }

  private setFormValue() {
    if (!this.purchaseReport) {
      this.addPurchase();
      return;
    }

    let purchases = this.reportForm.get('purchases') as FormArray;

    this.purchaseReport.purchases.map((purchase, index) => {
      this.addPurchase();
      purchases.at(index).get('species').setValue(purchase.foodSpecies || purchase.aqSpecies);

    });

    this.purchaseReport.amendments.map(() => {
      this.addAmendment();
    });

    this.initialDuplicatesCheckParams = DuplicatesCheckParams.fromFormObject([
      this.purchaseReport.reportDate.format('YYYY-MM-DD'),
      this.purchaseReport.reportType,
      this.purchaseReport.seller
    ]);

    this.reportForm.patchValue(this.purchaseReport);
    this.updatePurchaseValuesList();
    if ( this.period ) {
      this.reportForm.get('reportDate').setValue(this.purchaseReport.reportDate.format('YYYY-MM-DD'));
    } else {
      this.reportForm.get('reportDate').setValue(DatePickerFormatter.prototype.parse(this.purchaseReport.reportDate.format('MM/DD/YYYY')));
    }
    this.seller = this.purchaseReport.seller;

    this.initPurchaseValidators(this.reportForm.get('reportType'));
    this.initVesselValidators();
    FormControlUtils.markAllTouched(this.reportForm, false);
    this.sellerStatus = LicenseStatus.ACTIVE;
    this.updateTotal();

  }

  private addPurchaseReport(addAnother: boolean = false) {
    this.loading = true;
    this.reportProcessor.addPurchaseReport(this.getReport(), this.getDealerId())
      .subscribe(() => {
        if (addAnother) {
          this.reportAdded = true;
          this.purchaseWarnings = {};
          this.reportTotal = Big(0);
          this.lastDuplicatesCheckParams = new DuplicatesCheckParams();
          const reportType = this.reportForm.get('reportType').value,
            seller = this.reportForm.get('seller').value;
          this.resetForm();
          this.reportForm.get('seller').patchValue(seller);
          this.reportForm.get('reportType').patchValue(reportType);
          this.reportForm.controls['reportType'].markAsDirty();
          document.getElementsByClassName('modal')[0].scrollTop = 0;
        } else {
          this.activeModal.close(true);
        }
        const modal = this.modalService.open(ConfirmationComponent, { backdrop: true, keyboard: true });
        modal.componentInstance.confirmationSuccess = true;
        modal.componentInstance.confirmationTitle = 'Your Purchase Transaction Dealer Report was added successfully.';
        this.loading = false;
      }, this.invalidRequestHandler);
  }

  private editPurchaseReport() {
    this.loading = true;
    this.reportProcessor.editPurchaseReport(this.purchaseReport.id, this.getReport())
      .pipe(takeUntil(this.destroyed$))
      .subscribe((editedReportId: number) => {
        this.activeModal.close(editedReportId);
        this.loading = false;
      }, this.invalidRequestHandler);
  }

  private invalidRequestHandler = (error: AppError | HttpErrorResponse) => {
    this.loading = false;
    let errorMatched = false;
    if ((error instanceof HttpErrorResponse) && (400 === (error as HttpErrorResponse).status)) {
      if(error.error && typeof error.error === 'object' && Array.isArray(error.error)) {
        (error as AppError).error.forEach(
          (value: AppErrorDto) => {
            if (value.field) {
              const control: AbstractControl = this.reportForm.get(value.field);
              if (control && value.code) {
                const err: ValidationErrors = {};
                err[value.code] = true;
                control.setErrors(err,{ emitEvent: false });
                errorMatched = true;
              }
            }
          });
      }
    }
    if (!errorMatched) {
      throw error;
    }
  }

  private getReport() {
    const notifyFisher = this.reportForm.get('notifyFisher').value;
    const reportDate = this.reportForm.get('reportDate').value;
    const reportType = this.reportForm.get('reportType').value;

    const report = Object.assign({}, this.reportForm.value,
      {
        notifyFisher: notifyFisher ? true : false,
        sellerId: this.seller.licenseNumber,
        reportDate: DateFormatter.formatDateBeforeSend(reportDate)
      });

    report.purchases = report.purchases.map(
      (purchase) => {
        const result = Object.create(Purchase.prototype);
        Object.assign(result, purchase);

        const species = purchase.species;
        switch (reportType) {
          case ReportType.P:
            result.foodSpecies = new FoodSpecies(species.id);
            break;
          case ReportType.PAQ:
            result.aqSpecies = new AqSpecies(species.id);
            break;

          default:
            break;
        }
        delete result.species;

        if (reportType === ReportType.P) {
          const condition = purchase.condition;
          if (condition) {
            result.condition = {code: condition.code};
          }
        } else {
          delete result.condition;
        }

        const pcsBased = (ReportType.PAQ === reportType) || (species && species.lbsExempt);
        if (pcsBased) {
          delete result.lbsBought;
          delete result.pricePerLb;
        } else {
          delete result.pricePerPc;
        }
        return result;
      });

    return report;
  }

  private updatePurchaseValue(purchase: AbstractControl) {
    const reportType = this.reportForm.get('reportType').value;
    const species = purchase.get('species').value as Species;

    if (this.requiresPcs(reportType, species)) {
      const value = purchase.get('value');
      const piecesBought = purchase.get('piecesBought');
      const pricePerPc = purchase.get('pricePerPc');

      if (piecesBought.valid && piecesBought.value && pricePerPc.valid && pricePerPc.value) {
        value.setValue(multiplyBig(piecesBought.value, pricePerPc.value).round(2, CURRENCY_ROUNDING_MODE), {emitEvent: false});
        value.updateValueAndValidity();
      }
    } else {
      const value = purchase.get('value');
      const lbsBought = purchase.get('lbsBought');
      const pricePerLb = purchase.get('pricePerLb');

      if (lbsBought.valid && lbsBought.value && pricePerLb.valid && pricePerLb.value) {
        value.setValue(multiplyBig(lbsBought.value, pricePerLb.value).round(2, CURRENCY_ROUNDING_MODE), {emitEvent: false});
        value.updateValueAndValidity();
      }
    }
  }

  private updatePurchasePPU(purchase: AbstractControl) {
    const reportType = this.reportForm.get('reportType').value;
    const species = purchase.get('species').value as Species;

    if (this.requiresPcs(reportType, species)) {
      const value = purchase.get('value');
      const piecesBought = purchase.get('piecesBought');
      const pricePerPc = purchase.get('pricePerPc');

      if (piecesBought.valid && piecesBought.value && value.valid && value.value) {
        pricePerPc.setValue(divideBig(value.value, piecesBought.value).round(2, CURRENCY_ROUNDING_MODE), {emitEvent: false});
        pricePerPc.updateValueAndValidity();
      }
    } else {
      const value = purchase.get('value');
      const lbsBought = purchase.get('lbsBought');
      const pricePerLb = purchase.get('pricePerLb');

      if (lbsBought.valid && lbsBought.value && value.valid && value.value) {
        pricePerLb.setValue(divideBig(value.value, lbsBought.value).round(2, CURRENCY_ROUNDING_MODE), {emitEvent: false});
        pricePerLb.updateValueAndValidity();
      }
    }
  }

  /**
   * @return max allowed date (inclusive) for the purchase report, determined based on reporting period or reporting
   *         year
   */
  private reportDateMax(): Date {
    let result = null;
    if (this.period) {
      result = moment(this.period.lastDay()).startOf('day').toDate();
    } else if (this.selectedYear) {
      const today = moment();
      const currentYear = today.year();
      if (this.selectedYear < currentYear) {
        result = moment([this.selectedYear]).month(11).date(31).startOf('day').toDate();
        result = this.getSatarday(result);
      } else {
        result = moment().startOf('day').toDate();
      }
    } else {
      result = moment().startOf('day').toDate();
    }
    return result;
  }

  /**
   * @return min allowed date (inclusive) for the purchase report, determined based on reporting period or reporting
   *         year
   */
  private reportDateMin(): Date {
    const result = this.period ?
      moment(this.period.start) :
      (this.selectedYear ?
        moment([this.selectedYear]) :
        (this.profile && this.profile.bulkDataReport === true && this.purchaseReport.reportDate ?
          moment(this.purchaseReport.reportDate).dayOfYear(1) :
          moment().dayOfYear(1)));
    return this.getSunday(result.startOf('day').toDate());
  }

  private setSpeciesValueInPurchase(index: number) {
    if (!this.purchaseReport) {
      return '';
    }

    const purchase = this.purchaseReport.purchases[index];

    return purchase.foodSpecies || purchase.aqSpecies;
  }

  private validateReportType() {
    if (this.reportForm.get('reportType').hasError('invalidSpecies')) {
      this.reportForm.get('reportType').updateValueAndValidity();
    }
  }
  toggleVesselValidation($) {
    this.initVesselValidators();
    this.reportForm.get('vessel.name').setValue('');
    this.reportForm.get('vessel.id').setValue('');
  }
}
